/**
 * Body of POST PendingTasks. The saga adds `companyId` (selected company) from the store, plus
 * `lang` (current language) for the search endpoint only. The backend takes the assignee from the
 * token, so it is not sent.
 *
 * Filters without a value are left out (undefined, so JSON.stringify drops them): the body only
 * shows the filters that are applied.
 *
 * @typedef {Object} PendingTasksRequest
 * @property {number} pageNumber       1-based page
 * @property {number} recordsByPage    page size
 * @property {string} [searchParam]    free-text search (omitted = none)
 * @property {string} [startDateTime]  dd/MM/yyyy (omitted = none)
 * @property {string} [endDateTime]    dd/MM/yyyy (omitted = none)
 * @property {string} sortField        column to sort by
 * @property {"asc"|"desc"} sortDirection
 */

/**
 * Builds a PendingTasksRequest with defaults filled in.
 * @returns {PendingTasksRequest}
 */
export const createPendingTasksRequest = (params = {}) => ({
	pageNumber: params.pageNumber || 1,
	recordsByPage: params.recordsByPage || 10,
	searchParam: params.searchParam?.trim() || undefined,
	startDateTime: params.startDate || undefined,
	endDateTime: params.endDate || undefined,
	sortField: params.sortField || "modified_at",
	sortDirection: params.sortDirection || "desc",
});

/**
 * One task from POST PendingTasks (a tbl_workflow_tasks row). Field names stay snake_case
 * because the PendingTasks column config and button rules (tbl_table_column_configs) use them.
 * process_definition_key and task_name are keys; the table translates them (translateValue).
 *
 * @typedef {Object} PendingTaskResponse
 * @property {string} task_id
 * @property {string} process_instance_id
 * @property {string} process_definition_key
 * @property {string} task_definition_key
 * @property {string} task_name
 * @property {string|null} form_key
 * @property {string} assignee
 * @property {string} started_at
 * @property {string} modified_at
 * @property {boolean} can_delete   Delete would be allowed now (permission + the workflow's delete
 *                                  policy). Only shows/hides the button; the backend checks again.
 * @property {boolean} has_ready_documents  the process has a ready document: Download has something to give
 */

/**
 * Maps one task from api/WorkflowTasks/PendingTasks or .../PendingTasks/Search (camelCase, same
 * shape from both) to a PendingTaskResponse.
 * @returns {PendingTaskResponse}
 */
export const mapPendingTaskResponse = (task = {}) => ({
	task_id: task.taskId,
	process_instance_id: task.processInstanceId,
	process_definition_key: task.processDefinitionKey,
	task_definition_key: task.taskDefinitionKey,
	task_name: task.taskName,
	form_key: task.formKey ?? null,
	assignee: task.assignee,
	started_at: task.startedAt,
	modified_at: task.modifiedAt,
	can_delete: task.canDelete === true,
	has_ready_documents: task.hasReadyDocuments === true,
});

/**
 * Body of POST MyProcesses (workflow history, "My processes"). Same shape as PendingTasksRequest:
 * the saga adds `companyId`, plus `lang` for the search endpoint only; the user comes from the
 * token. Filters without a value are left out.
 *
 * @typedef {PendingTasksRequest} MyProcessesRequest
 */

/**
 * Builds a MyProcessesRequest with defaults filled in (newest activity first).
 * @returns {MyProcessesRequest}
 */
export const createMyProcessesRequest = (params = {}) => createPendingTasksRequest(params);

/**
 * One process of "My processes" (a tbl_workflow_instances row). Field names stay snake_case
 * because the WorkflowHistory column config and button rules (tbl_table_column_configs) use them.
 * process_definition_key and task_name are keys; the table translates them (translateValue).
 *
 * @typedef {Object} MyProcessResponse
 * @property {string} process_instance_id
 * @property {string} process_definition_key
 * @property {string} state                ACTIVE, COMPLETED, EXTERNALLY_TERMINATED, ...
 * @property {string|null} task_name       the current task (empty once the process ended)
 * @property {string|null} current_assignee
 * @property {string|null} start_user_id
 * @property {string|null} generated_id    the workflow's own business id, when it has one
 * @property {string|null} started_at
 * @property {string|null} modified_at
 * @property {string|null} ended_at
 * @property {boolean} is_starter          why it is in my history: I started it ...
 * @property {boolean} has_completed_task  ... and/or I completed one of its tasks
 * @property {boolean} can_delete          I could delete it now (delete_workflow + the workflow's delete policy)
 * @property {boolean} can_reassign        I could reassign its task now (reassign_task + a reassignable step that has an assignee)
 * @property {boolean} has_documents       it has at least one generated document (any status): Regenerate has something to work on
 * @property {boolean} has_ready_documents it has at least one ready document: Download has something to give
 */

/**
 * Maps one process from api/WorkflowProcesses/MyProcesses or .../Search (camelCase, same shape
 * from both) to a MyProcessResponse.
 * @returns {MyProcessResponse}
 */
export const mapMyProcessResponse = (process = {}) => ({
	process_instance_id: process.processInstanceId,
	process_definition_key: process.processDefinitionKey,
	state: process.state,
	task_name: process.taskName ?? null,
	current_assignee: process.currentAssignee ?? null,
	start_user_id: process.startUserId ?? null,
	generated_id: process.generatedId ?? null,
	started_at: process.startedAt ?? null,
	modified_at: process.modifiedAt ?? null,
	ended_at: process.endedAt ?? null,
	is_starter: process.isStarter === true,
	has_completed_task: process.hasCompletedTask === true,
	can_delete: process.canDelete === true,
	can_reassign: process.canReassign === true,
	has_documents: process.hasDocuments === true,
	has_ready_documents: process.hasReadyDocuments === true,
});

/**
 * One generated document of a process (POST api/WorkflowTasks/Documents), e.g. one meeting report.
 * Only "ready" documents can be downloaded; "pending" is still being generated, "failed" can be
 * retried by an admin.
 *
 * @typedef {Object} ProcessDocumentResponse
 * @property {string} documentId
 * @property {string} displayName   name to show (falls back to "Document")
 * @property {"pending"|"ready"|"failed"} status
 * @property {string|null} taskId   the task run it was made for (e.g. which meeting)
 * @property {string|null} createdAt
 * @property {number} version   1 for the first render, +1 per regeneration
 * @property {string|null} versionCreatedAt   when the current version was made
 */

/**
 * Maps one document from POST api/WorkflowTasks/Documents to a ProcessDocumentResponse.
 * @returns {ProcessDocumentResponse}
 */
export const mapProcessDocumentResponse = (document = {}) => ({
	documentId: document.documentId,
	displayName: document.displayName || "Document",
	status: document.status,
	taskId: document.taskId ?? null,
	createdAt: document.createdAt ?? null,
	version: document.version ?? 1,
	versionCreatedAt: document.versionCreatedAt ?? null,
});
